import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router';
import {
  Button,
  FormControl,
  Grid,
  Typography,
} from '@mui/material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import { useEffect, useState } from 'react';

import {
  selectUnidadeSelecionada,
  selectUnidadesDeNegocioAssociadas,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import { useListClientes } from '../../../common/hooks/useListClientes';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { generateProcessosEmAndamentoReport } from './processosEmAndamentoPage.helpers';

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '50vw',
      minHeight: '40vh',
    },
  },
});

const ProcessosEmAndamentoPage = ({
  title,
  isOpen,
  closeModal,
}: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  keycloak.updateToken(300);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const tenantList = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [businessError, setBusinessError] = useState(false);
  const [datas, setDatas] = useState({
    dataInicio: '',
    dataFim: '',
  });

  useUnidadeNegocioGuard();

  const handleTenantsChange = (
    _e: any,
    value: SimpleMultiSelectOption[] | null
  ) => setSelectedTenants(value || []);

  const handleClientsChange = (
    _e: any,
    value: SimpleMultiSelectOption[] | null
  ) => setSelectedClients(value || []);

  const handleChangeDataInicio = (iso: string) =>
    setDatas((p) => ({ ...p, dataInicio: iso.split('T')[0] }));

  const handleChangeDataFim = (iso: string) =>
    setDatas((p) => ({ ...p, dataFim: iso.split('T')[0] }));

  const validarDatas = () =>
    datas.dataInicio !== '' &&
    datas.dataFim !== '' &&
    new Date(datas.dataInicio) <= new Date(datas.dataFim);

  const handleSubmit = async () => {
    keycloak.updateToken(300);

    if (!validarDatas()) {
      dispatch(setErrorFeedback({ message: 'Preencha as datas corretamente.' }));
      return;
    }

    if (selectedTenants.length === 0) {
      setBusinessError(true);
      dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade.' }));
      return;
    }
    setBusinessError(false);

    if (token && selectedUnit) {
      const tenantIds = selectedTenants.map((t) => t.id.toString());
      const clientIds = selectedClients.map((c) => c.id.toString());

      const erro = await generateProcessosEmAndamentoReport(
        token,
        selectedUnit,
        tenantIds,
        clientIds,
        datas.dataInicio,
        datas.dataFim
      );

      if (erro == null) {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
      } else {
        dispatch(setErrorFeedback({ message: erro }));
      }
    }

    history.push('/exportacao/relatorios');
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Form onSubmit={() => {}}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={businessError}>
                    <MultiSelectStyled
                      controlledValues={selectedTenants}
                      options={tenantList.map((t: { id: { toString: () => any; }; pessoa: { nomeResumido: any; }; }) => ({
                        id: t.id.toString(),
                        value: t.pessoa.nomeResumido,
                      }))}
                      onChangeAction={handleTenantsChange}
                      label="Unidade de Negócio"
                    />
                    {selectedTenants.length === 0 && (
                      <Typography variant="body2" color="error">
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedClients}
                      options={clientes.map((c) => ({
                        id: c.id ?? '',
                        value: `(${c.pessoa!.cnpj}) ${c.pessoa!.nome}`,
                      }))}
                      onChangeAction={handleClientsChange}
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="startDate"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="proc-em-andamento-start"
                        name={input.name}
                        label="Data de Início"
                        required
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangeDataInicio(d.toISOString());
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="endDate"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="proc-em-andamento-end"
                        name={input.name}
                        label="Data Final"
                        required
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangeDataFim(d.toISOString());
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={4}>
          <Button variant="outlined" color="primary" onClick={closeModal}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            fullWidth
            disabled={
              datas.dataFim === '' ||
              datas.dataInicio === '' ||
              selectedTenants.length === 0
            }
          >
            Gerar Relatório
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessosEmAndamentoPage;
